import { FC, useEffect, useRef, useState } from "react";
import { Flex, Box } from "@smile2impress/components/relaunch";
import { motion, PanInfo, useMotionValue } from "framer-motion";
import { ColumnBeforeAndAfter } from "templates/relaunch/MultiColumnSlider/components";
import { DirectionType } from "templates/relaunch/MultiColumnSlider/MultiColumnSlider.types";

import { SwiperProps } from "./Swiper.types";

export const Swiper: FC<SwiperProps> = ({
    items,
    withExpansion = false,
    itemComponent,
    itemContent,
    direction = DirectionType.Vertical,
}) => {
    const [activeSlide, setActiveSlide] = useState(0);
    const swiperRef = useRef<HTMLDivElement | null>(null);

    const onDragEnd = (_event: PointerEvent, info: PanInfo) => {
        const left = info.offset.x > 0;
        setActiveSlide((prev) => {
            const newSlide = left
                ? prev > 0
                    ? prev - 1
                    : prev
                : prev < items.length - 1
                ? prev + 1
                : prev;

            return newSlide;
        });
    };

    const Component = itemComponent || ColumnBeforeAndAfter;
    type ComponentProps = React.ComponentProps<typeof Component>;

    useEffect(() => {
        swiperRef?.current?.style.setProperty(
            "left",
            `${
                ((swiperRef?.current?.scrollWidth || 0) / items.length) *
                activeSlide *
                -1
            }px`
        );
    }, [activeSlide, items?.length]);

    const selectSlide = (index: number) => {
        setActiveSlide(index);
    };

    return (
        <Box>
            <Box overflow="hidden">
                <motion.div
                    style={{ x: useMotionValue(0) }}
                    drag="x"
                    dragConstraints={{ left: 0, right: 0 }}
                    onPanEnd={onDragEnd}
                >
                    <Flex
                        alignItems="flex-start"
                        direction="row"
                        flexWrap="nowrap"
                        width={`calc(${items.length} * 100vw)`}
                        position="relative"
                        transition="left .2s ease-in-out"
                        ref={swiperRef}
                    >
                        {items.map((item, index) => (
                            <Flex width={["100vw"]} key={index}>
                                <Box px={[2]} width="100%">
                                    <Component
                                        {...(item as ComponentProps)}
                                        {...itemContent}
                                        withExpansion={withExpansion}
                                        direction={direction}
                                    />
                                </Box>
                            </Flex>
                        ))}
                    </Flex>
                </motion.div>
            </Box>

            <Flex
                columnGap="2px"
                justifyContent={"center"}
                width="100vw"
                px={2}
                pb="6px"
            >
                {items.map((_item, index) => (
                    <Flex
                        key={index}
                        onClick={() => selectSlide(index)}
                        bgColor="base.gunmetal"
                        opacity={activeSlide === index ? 1 : 0.2}
                        w={activeSlide === index ? "20px" : "8px"}
                        h="8px"
                        borderRadius="8px"
                        transition="all .2s ease-in-out"
                    ></Flex>
                ))}
            </Flex>
        </Box>
    );
};
